import { Controller } from "@api/config";
import type { IRequestConfig } from "@api/config";

import type { IPlayRequest, IUpdateMotionRequest } from "./request.types";
import type { TGetCapabilitiesResponse, TGetToysSessionResponse } from "./response.types";

class ToysGateController extends Controller {
	public constructor() {
		super({ requestUrl: "api/v1/" });
	}

	/**
	 * @GET TOYS SESSION
	 * @description Returns the session bound to the token. Reconnect of a suspended device re-arms the stopped state, it never resumes motion.
	 */
	public async getToysSessionAsync({ token, signal }: IRequestConfig) {
		return this.get<TGetToysSessionResponse>("toysGate", "session", {
			token: null,
			signal,
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
	}

	/**
	 * @GET CAPABILITIES
	 * @description Declares the session device actuators and partner presets; clients render controls from this declaration.
	 */
	public async getCapabilitiesAsync({ token, signal }: IRequestConfig) {
		return this.get<TGetCapabilitiesResponse>("toysGate", "capabilities", {
			token: null,
			signal,
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
	}

	/**
	 * @PATCH UPDATE MOTION
	 * @description Updates speed or range. While running the change is applied to the device on the fly, otherwise it is stored for the next play.
	 */
	public async updateMotionAsync({ token, motion, preset_id }: IUpdateMotionRequest) {
		return this.patch<TGetToysSessionResponse>(
			"session/motion",
			{ motion, preset_id },
			{
				token: null,
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
			"toysGate",
		);
	}

	/**
	 * @POST ACCEPT
	 * @description The opposite side grants control. The device stays stopped: motion needs an explicit play.
	 */
	public async acceptAsync({ token }: IRequestConfig) {
		return this.post<TGetToysSessionResponse>(
			"session/accept",
			null,
			{
				token: null,
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
			"toysGate",
		);
	}

	/**
	 * @POST PLAY
	 * @description Applies the given motion or preset on top of current settings and starts the device.
	 */
	public async playAsync({ token, motion, preset_id }: IPlayRequest) {
		return this.post<TGetToysSessionResponse>(
			"session/play",
			{ motion, preset_id },
			{
				token: null,
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
			"toysGate",
		);
	}

	/**
	 * @POST STOP
	 * @description Stops the device but keeps consent granted. Local state changes immediately; the vendor stop is confirmed with retries.
	 */
	public async stopAsync({ token }: IRequestConfig) {
		return this.post<TGetToysSessionResponse>(
			"session/stop",
			null,
			{
				token: null,
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
			"toysGate",
		);
	}

	/**@POST DECLINE */
	public async declineAsync({ token }: IRequestConfig) {
		return this.post<TGetToysSessionResponse>(
			"session/decline",
			null,
			{ token: null, headers: { Authorization: `Bearer ${token}` } },
			"toysGate",
		);
	}
}

export const toysGateAPI = new ToysGateController();
